import React, { useState } from 'react';
import { ShieldCheck, AlertTriangle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const AgeDeclarationModal = () => {
  const { user, acceptAgeDeclaration, logout } = useAuth();
  const [accepted, setAccepted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Only show for logged-in non-admin users who still owe consent for the
  // current privacy-policy version (backend-computed `consentRequired`).
  if (!user || user.role === 'admin' || user.role === 'super_admin' || !user.consentRequired) return null;

  const handleConfirm = async () => {
    if (!accepted) {
      setError('Please check the box to confirm your age before continuing.');
      return;
    }
    setIsSubmitting(true);
    setError('');
    const result = await acceptAgeDeclaration();
    if (!result.success) {
      setError('Something went wrong. Please try again.');
      setIsSubmitting(false);
    }
    // On success, user state is updated in context and modal unmounts automatically
  };

  const handleDecline = async () => {
    await logout();
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm px-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8 animate-in fade-in zoom-in-95 duration-200">
        {/* Icon */}
        <div className="flex justify-center mb-5">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center">
            <ShieldCheck className="w-8 h-8 text-amber-600" />
          </div>
        </div>

        <h2 className="text-2xl font-bold text-gray-800 text-center mb-2">Age Verification Required</h2>
        <p className="text-gray-500 text-sm text-center mb-6">
          BookMyFresh requires all users to confirm their age to continue using the platform.
        </p>

        {/* Declaration checkbox */}
        <div
          className={`flex items-start gap-3 p-4 rounded-xl border-2 mb-4 cursor-pointer transition-colors ${
            accepted ? 'border-[#548B2E] bg-green-50' : 'border-gray-200 bg-gray-50 hover:border-gray-300'
          }`}
          onClick={() => setAccepted(prev => !prev)}
        >
          <div className={`w-5 h-5 mt-0.5 flex-shrink-0 rounded border-2 flex items-center justify-center transition-colors ${
            accepted ? 'bg-[#548B2E] border-[#548B2E]' : 'border-gray-300 bg-white'
          }`}>
            {accepted && (
              <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            )}
          </div>
          <span className="text-sm text-gray-800 select-none">
            <span className="font-semibold">I confirm that I am 18 years of age or older.</span>
            <span className="block text-xs text-gray-500 mt-0.5">
              This declaration is recorded with a timestamp as required by our Privacy Policy.
            </span>
          </span>
        </div>

        {error && (
          <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg mb-4">
            <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        <button
          onClick={handleConfirm}
          disabled={isSubmitting}
          className="w-full bg-[#548B2E] hover:bg-[#3f6b20] text-white py-3 rounded-xl font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 mb-3"
        >
          {isSubmitting ? (
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <>
              <ShieldCheck className="w-4 h-4" />
              Confirm & Continue
            </>
          )}
        </button>

        <button
          onClick={handleDecline}
          disabled={isSubmitting}
          className="w-full text-sm text-gray-400 hover:text-gray-600 py-2 transition-colors disabled:cursor-not-allowed"
        >
          I am under 18 — Sign out
        </button>
      </div>
    </div>
  );
};

export default AgeDeclarationModal;
